import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, Sparkles, Coins, Swords, Users, Home as HomeIcon,
  ScrollText, Hammer, ChevronDown, CircleDot,
} from "lucide-react";

const HERO_IMG = "/generated/hero_landing.png";

const PILLARS = [
  { icon: Sparkles, kicker: "Disciplines",  title: "23 skills, one wanderer.",          body: "Gathering, artisan, support and combat trees feed the same character. Cross-train freely — the world doesn't lock you into an archetype." },
  { icon: Coins,    kicker: "Economy",       title: "Real prices, real market.",         body: "Grand Exchange with escrow, taxes and price discovery. Every log, ore and bar carries value other wanderers set in real time." },
  { icon: Swords,   kicker: "Combat",        title: "600ms ticks with weight.",          body: "Server-authoritative rolls. Accuracy versus evasion, prayer curves, gear tiers, damage reduction — the fights matter." },
  { icon: ScrollText, kicker: "Progression", title: "Offline that respects you.",        body: "Close the tab. Live your life. Come back to 24 hours of banked XP, yields and loot — no punishment for stepping away." },
  { icon: Users,    kicker: "Together",      title: "A world you share.",                body: "Chat, whispers and clans from day one. Grand Exchange listings and seasonal events tie every wanderer's ledger to the next." },
  { icon: HomeIcon, kicker: "Home",          title: "Build a place to return to.",       body: "Construction unlocks regional-themed housing plots with functional rooms that quietly reward every session you play." },
];

function EyebrowTag({ children, testid }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid={testid}>
      <span className="w-6 h-px bg-amber-700/70" />
      <span className="text-[10px] uppercase tracking-[0.42em] font-numeric text-amber-500/85">
        {children}
      </span>
    </div>
  );
}

/* Refined amber CTA — no plastic-orange fill, subtle bevel + inner glow. */
function PrimaryCta({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative inline-flex items-center gap-2.5 px-6 py-3 rounded-[3px] font-numeric text-[11px] uppercase tracking-[0.28em] text-amber-100 transition-transform duration-300 hover:-translate-y-0.5"
      style={{
        background:
          "linear-gradient(180deg, #4a3618 0%, #2c1e0d 100%)",
        boxShadow:
          "inset 0 1px 0 rgba(245, 200, 120, 0.35), inset 0 -1px 0 rgba(0,0,0,0.5), 0 12px 30px -18px rgba(200, 140, 40, 0.7)",
        border: "1px solid rgba(180, 140, 60, 0.55)",
      }}
    >
      {children}
      <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" strokeWidth={2} />
    </Link>
  );
}

function GhostCta({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 px-2 py-3 font-numeric text-[11px] uppercase tracking-[0.28em] text-stone-400 hover:text-amber-300 transition-colors"
    >
      <span className="border-b border-stone-700 group-hover:border-amber-600 pb-0.5">{children}</span>
    </Link>
  );
}

/* Small mock "world card" for the hero right column — proves the game exists. */
function LiveWorldCard() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 600);
    return () => clearInterval(id);
  }, []);
  const events = [
    "Loremaster fells an oak · +38 XP",
    "Sena forges an iron scimitar",
    "Turael issues a Slayer task · 8 goblins",
    "Bronze bar × 12 listed on the Exchange",
    "Loremaster equips Bronze platebody",
    "Frost Vigil task cleared · +500 Firemaking",
    "Marra completes 12 Trout · +900 XP",
  ];
  const visible = events.slice(0, 5).map((line, i) => ({ line, id: (tick + i) % events.length }));

  return (
    <div
      className="relative w-full max-w-md p-6 rounded-[6px]"
      data-testid="live-world-card"
      style={{
        background:
          "linear-gradient(180deg, rgba(28, 22, 14, 0.85) 0%, rgba(16, 12, 8, 0.95) 100%)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(180, 140, 60, 0.22)",
        boxShadow: "0 40px 80px -30px rgba(0,0,0,0.9), inset 0 1px 0 rgba(245, 200, 120, 0.06)",
      }}
    >
      <div className="flex items-center gap-3 pb-3 mb-3 border-b border-stone-800/70">
        <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] animate-pulse" />
        <div className="flex-1">
          <div className="font-numeric text-[9px] uppercase tracking-[0.32em] text-stone-500">
            World feed · live
          </div>
          <div className="font-display text-base text-stone-100 leading-none mt-0.5">
            Territoria · Realm I
          </div>
        </div>
        <div className="text-right">
          <div className="font-numeric text-xl text-amber-300 leading-none">137</div>
          <div className="font-numeric text-[9px] uppercase tracking-[0.28em] text-stone-500 mt-0.5">Online</div>
        </div>
      </div>

      <ul className="space-y-2 mb-4">
        {visible.map((e) => (
          <li key={e.id + e.line} className="flex items-center gap-2 text-[12px] text-stone-300 leading-tight font-body">
            <CircleDot size={9} className="text-amber-500/70 shrink-0" />
            <span className="truncate">{e.line}</span>
          </li>
        ))}
      </ul>

      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-stone-800/70">
        {[
          { v: "23", l: "Skills" },
          { v: "6+", l: "Zones" },
          { v: "600ms", l: "Tick" },
        ].map((s) => (
          <div key={s.l} className="text-center">
            <div className="font-display text-lg text-amber-200 leading-none">{s.v}</div>
            <div className="font-numeric text-[9px] uppercase tracking-[0.28em] text-stone-500 mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[color:var(--bg-void)] text-stone-100 font-body relative overflow-x-hidden">

      {/* Subtle grain + warm radial glow overlays on the entire page */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 900px 700px at 20% 10%, rgba(200, 140, 40, 0.08) 0%, transparent 55%)," +
            "radial-gradient(ellipse 800px 600px at 80% 90%, rgba(120, 60, 20, 0.06) 0%, transparent 55%)",
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Header */}
      <header
        className="relative z-20 px-6 lg:px-14 py-5 flex items-center gap-6"
        data-testid="landing-header"
      >
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand">
          <img
            src="/apple-touch-icon.png"
            alt="Territoria: Life"
            className="w-9 h-9 rounded transition-transform duration-500 group-hover:rotate-6"
          />
          <div className="leading-[0.9]">
            <div className="font-display text-lg text-stone-100 tracking-wide">Territoria</div>
            <div className="font-numeric text-[8px] uppercase tracking-[0.4em] text-stone-500 mt-0.5">
              Chapter of Beginnings
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6 mx-auto">
          {[
            ["World",   "/#pillars"],
            ["Codex",   "/#pillars"],
            ["Rankings","/#pillars"],
            ["Journal", "/#pillars"],
          ].map(([label, to]) => (
            <Link
              key={label}
              to={to}
              className="text-[11px] uppercase tracking-[0.28em] text-stone-500 hover:text-amber-200 transition-colors"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/login"
            className="text-[11px] uppercase tracking-[0.28em] text-stone-400 hover:text-amber-200 transition-colors"
            data-testid="nav-login"
          >
            Sign in
          </Link>
          <PrimaryCta to="/register" testid="nav-register">Begin</PrimaryCta>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative z-10 px-6 lg:px-14 pt-8 lg:pt-16 pb-24 lg:pb-32"
        data-testid="landing-hero"
      >
        {/* Faint hero art far behind the copy, heavily muted */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.9) 0%, rgba(12,10,9,0.98) 70%, #0c0a09 100%), url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center top",
            filter: "saturate(0.5)",
          }}
        />

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-7xl mx-auto">
          {/* Left column */}
          <div className="lg:col-span-7">
            <EyebrowTag testid="hero-tag">A persistent painted-fantasy MMO · Tick 600ms</EyebrowTag>

            <h1
              className="font-display leading-[1.02] mt-8 mb-8 text-stone-100"
              style={{ fontSize: "clamp(2.6rem, 5.6vw, 4.6rem)", letterSpacing: "-0.01em" }}
            >
              A world that keeps turning
              <br />
              <em className="not-italic text-amber-300" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                fontWeight: 500,
              }}>
                while you live yours.
              </em>
            </h1>

            <p className="text-stone-400 text-[17px] leading-[1.7] max-w-lg mb-10">
              Grow twenty-three disciplines. Forge a market. Come back tomorrow to a
              realm that remembered you. No installs. No wipes. No pay-to-win.
            </p>

            <div className="flex flex-wrap items-center gap-5">
              <PrimaryCta to="/register" testid="cta-primary">Forge your character</PrimaryCta>
              <Link
                to="/login"
                data-testid="cta-secondary"
                className="group text-[11px] uppercase tracking-[0.28em] text-stone-400 hover:text-amber-200 transition-colors border-b border-stone-800 hover:border-amber-700 pb-1"
              >
                Return to your adventure
              </Link>
            </div>

            {/* Trust row */}
            <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-[10px] uppercase tracking-[0.28em] font-numeric text-stone-600">
              <div className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-600" />
                Server-authoritative
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-600" />
                24-hour offline cap
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-600" />
                No pay-to-win
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-600" />
                Browser-first
              </div>
            </div>
          </div>

          {/* Right column — the world card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <LiveWorldCard />
          </div>
        </div>

        {/* Chevron scroll hint */}
        <div className="relative mt-16 flex justify-center opacity-60">
          <ChevronDown size={18} className="text-amber-700 animate-bounce" />
        </div>
      </section>

      {/* Pillars */}
      <section id="pillars" className="relative z-10 px-6 lg:px-14 py-24" data-testid="landing-features">
        <div className="max-w-7xl mx-auto">
          <EyebrowTag>Chapter I · The pillars</EyebrowTag>
          <h2
            className="font-display text-4xl md:text-5xl lg:text-[3.2rem] mt-6 mb-14 max-w-2xl leading-[1.05] text-stone-100"
            style={{ letterSpacing: "-0.01em" }}
          >
            Six systems.{" "}
            <em className="not-italic text-amber-300" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontWeight: 500,
            }}>One living realm.</em>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {PILLARS.map(({ icon: Icon, kicker, title, body }, i) => (
              <article
                key={title}
                className="group border-t pt-6 transition-colors duration-300"
                style={{ borderColor: "rgba(180, 140, 60, 0.25)" }}
                data-testid={`feature-${kicker.toLowerCase()}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="font-numeric text-[9px] uppercase tracking-[0.32em] text-amber-600/80">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 h-px bg-stone-800" />
                  <Icon size={14} className="text-amber-500/80" strokeWidth={1.4} />
                </div>
                <div className="font-numeric text-[9px] uppercase tracking-[0.35em] text-amber-600/80 mb-2">
                  {kicker}
                </div>
                <h3 className="font-display text-2xl text-stone-100 mb-3 leading-tight" style={{ letterSpacing: "-0.005em" }}>
                  {title}
                </h3>
                <p className="text-stone-400 text-[14.5px] leading-[1.7]">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Closing invitation — refined "quote"-style */}
      <section className="relative z-10 px-6 lg:px-14 py-32">
        <div className="max-w-3xl mx-auto text-center">
          <EyebrowTag>Now open</EyebrowTag>
          <blockquote
            className="font-display text-3xl md:text-4xl lg:text-[2.6rem] mt-8 mb-8 leading-[1.15] text-stone-100"
            style={{ letterSpacing: "-0.01em" }}
          >
            &ldquo;Your first hour is free.
            <br />
            <em className="not-italic text-amber-300" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontWeight: 500,
            }}>Your ten thousandth is what we build for.&rdquo;</em>
          </blockquote>
          <p className="text-stone-500 text-[15px] leading-relaxed max-w-xl mx-auto mb-10">
            No install. No pay-to-win. Just tick after tick of quiet, satisfying
            progression in a world other wanderers share with you right now.
          </p>
          <PrimaryCta to="/register" testid="cta-footer">Forge your character</PrimaryCta>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 lg:px-14 py-10 flex flex-wrap items-center justify-between gap-4 border-t border-stone-900">
        <div className="flex items-center gap-3">
          <img src="/favicon-32.png" alt="" className="w-5 h-5 rounded opacity-70" />
          <span className="text-stone-500 text-[11px] font-numeric">
            © {new Date().getFullYear()} Territoria: Life · All ledgers persistent
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.32em] font-numeric text-stone-600">
          Tick · 600 ms · Offline cap 24 h
        </div>
      </footer>
    </div>
  );
}
